import React, { useEffect, useState } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import { ApplicationView } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import classNames from 'classnames';
import layoutStyles from './ToolLayoutSettings.module.css';
import layoutAlignmentHorizontalLeftTop from '../assets/icons/layoutAlignmentHorizontalLeftTop.svg';
import layoutAlignmentHorizontalCenterTop from '../assets/icons/layoutAlignmentHorizontalCenterTop.svg';
import layoutAlignmentHorizontalRightTop from '../assets/icons/layoutAlignmentHorizontalRightTop.svg';
import layoutAlignmentHorizontalLeftCenter from '../assets/icons/layoutAlignmentHorizontalLeftCenter.svg';
import layoutAlignmentHorizontalCenterCenter from '../assets/icons/layoutAlignmentHorizontalCenterCenter.svg';
import layoutAlignmentHorizontalRightCenter from '../assets/icons/layoutAlignmentHorizontalRightCenter.svg';
import layoutAlignmentHorizontalLeftBottom from '../assets/icons/layoutAlignmentHorizontalLeftBottom.svg';
import layoutAlignmentHorizontalCenterBottom from '../assets/icons/layoutAlignmentHorizontalCenterBottom.svg';
import layoutAlignmentHorizontalRightBottom from '../assets/icons/layoutAlignmentHorizontalRightBottom.svg';
import layoutAlignmentVerticalLeftTop from '../assets/icons/layoutAlignmentVerticalLeftTop.svg';
import layoutAlignmentVerticalCenterTop from '../assets/icons/layoutAlignmentVerticalCenterTop.svg';
import layoutAlignmentVerticalRightTop from '../assets/icons/layoutAlignmentVerticalRightTop.svg';
import layoutAlignmentVerticalLeftCenter from '../assets/icons/layoutAlignmentVerticalLeftCenter.svg';
import layoutAlignmentVerticalCenterCenter from '../assets/icons/layoutAlignmentVerticalCenterCenter.svg';
import layoutAlignmentVerticalRightCenter from '../assets/icons/layoutAlignmentVerticalRightCenter.svg';
import layoutAlignmentVerticalLeftBottom from '../assets/icons/layoutAlignmentVerticalLeftBottom.svg';
import layoutAlignmentVerticalCenterBottom from '../assets/icons/layoutAlignmentVerticalCenterBottom.svg';
import layoutAlignmentVerticalRightBottom from '../assets/icons/layoutAlignmentVerticalRightBottom.svg';
import { svgToIcon } from '../utils/svg';
import type LayoutBehaviors from '../objects/LayoutBehaviors';

const AlignGrid: React.FC<{
  alignment: [string, string];
  setCurrentAlignment: (h: string, v: string) => void;
  layoutDirection: string;
  layoutGap: string | number;
}> = ApplicationView(
  ({
    alignment: [currentH, currentV],
    setCurrentAlignment,
    layoutDirection,
    layoutGap,
  }) => {
    const s = layoutStyles;
    const [hoverH, setHoverH] = useState<string | null>(currentH);
    const [hoverV, setHoverV] = useState<string | null>(currentV);

    const cell = (h, v) => {
      if (
        layoutGap === 'auto' &&
        (layoutDirection === 'horizontal' ? h : v) !== 'center'
      ) {
        return null;
      } else {
        return (
          <div
            className={s.cell}
            onMouseOver={() => {
              setHoverH(h);
              setHoverV(v);
            }}
            onMouseOut={() => {
              setHoverH(null);
              setHoverV(null);
            }}
            onClick={() => {
              setCurrentAlignment(h, v);
            }}
          />
        );
      }
    };

    return (
      <div
        className={classNames(s.container, s[`direction-${layoutDirection}`], {
          [s.autoGap]: layoutGap === 'auto',
        })}
      >
        <div className={classNames(s[`h-${currentH}`], s[`v-${currentV}`])}>
          <div className={s.grid}>
            {cell('left', 'top')}
            {cell('center', 'top')}
            {cell('right', 'top')}
            {cell('left', 'center')}
            {cell('center', 'center')}
            {cell('right', 'center')}
            {cell('left', 'bottom')}
            {cell('center', 'bottom')}
            {cell('right', 'bottom')}
          </div>
          <div className={s.indicatorContainer}>
            <div className={s.indicator}>
              <div className={s.indicatorChild} />
              <div className={s.indicatorChild} />
              <div className={s.indicatorChild} />
            </div>
          </div>
        </div>
        {hoverH && hoverV && (hoverH !== currentH || hoverV !== currentV) && (
          <div className={classNames(s[`h-${hoverH}`], s[`v-${hoverV}`])}>
            <div className={classNames(s.indicatorContainer, s.hoverIndicator)}>
              <div className={s.indicator}>
                <div className={s.indicatorChild} />
                <div className={s.indicatorChild} />
                <div className={s.indicatorChild} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

interface PopupProps {
  selection: fabric.Object;
  alignment: [string, string];
  setCurrentAlignment: (h: string, v: string) => void;
}
const Popup: React.FC<PopupProps> = ApplicationView(
  ({ selection, alignment, setCurrentAlignment }) => {
    const layoutDirection = selection.firstObject().get('layoutDirection');
    const layoutGap = selection.firstObject().get('layoutGap');

    return (
      <AlignGrid
        alignment={alignment}
        setCurrentAlignment={setCurrentAlignment}
        layoutDirection={layoutDirection}
        layoutGap={layoutGap}
      />
    );
  }
);

const getIcon = (
  layoutDirection: LayoutBehaviors['layoutDirection'],
  alignment: [string, string]
) => {
  if (layoutDirection === 'horizontal') {
    switch (alignment.join(' ')) {
      case 'left top':
        return svgToIcon(layoutAlignmentHorizontalLeftTop);
      case 'center top':
        return svgToIcon(layoutAlignmentHorizontalCenterTop);
      case 'right top':
        return svgToIcon(layoutAlignmentHorizontalRightTop);
      case 'left center':
        return svgToIcon(layoutAlignmentHorizontalLeftCenter);
      case 'center center':
        return svgToIcon(layoutAlignmentHorizontalCenterCenter);
      case 'right center':
        return svgToIcon(layoutAlignmentHorizontalRightCenter);
      case 'left bottom':
        return svgToIcon(layoutAlignmentHorizontalLeftBottom);
      case 'center bottom':
        return svgToIcon(layoutAlignmentHorizontalCenterBottom);
      case 'right bottom':
        return svgToIcon(layoutAlignmentHorizontalRightBottom);
      default:
        return svgToIcon(layoutAlignmentHorizontalCenterCenter);
    }
  } else if (layoutDirection === 'vertical') {
    switch (alignment.join(' ')) {
      case 'left top':
        return svgToIcon(layoutAlignmentVerticalLeftTop);
      case 'center top':
        return svgToIcon(layoutAlignmentVerticalCenterTop);
      case 'right top':
        return svgToIcon(layoutAlignmentVerticalRightTop);
      case 'left center':
        return svgToIcon(layoutAlignmentVerticalLeftCenter);
      case 'center center':
        return svgToIcon(layoutAlignmentVerticalCenterCenter);
      case 'right center':
        return svgToIcon(layoutAlignmentVerticalRightCenter);
      case 'left bottom':
        return svgToIcon(layoutAlignmentVerticalLeftBottom);
      case 'center bottom':
        return svgToIcon(layoutAlignmentVerticalCenterBottom);
      case 'right bottom':
        return svgToIcon(layoutAlignmentVerticalRightBottom);
      default:
        return svgToIcon(layoutAlignmentVerticalCenterCenter);
    }
  } else {
    // We do not yet support wrap, so really we won't get to this case yet.
    return svgToIcon(layoutAlignmentHorizontalCenterCenter);
  }
};

// Configure all of the layout settings.
class ToolLayoutAlignment extends Tool {
  static toolName = 'Alignment';

  static buttonComponent() {
    const Button: ToolButtonComponent = ApplicationView(
      ({ selection, tool, controller }) => {
        const layoutDirection = selection.firstObject().get('layoutDirection');
        const [alignment, setAlignment] = useState<[string, string]>([
          selection.firstObject().get('layoutAlignHorizontal'),
          selection.firstObject().get('layoutAlignVertical'),
        ]);

        const setCurrentAlignment = (h, v) => {
          setAlignment([h, v]);
          selection.set('layoutAlignHorizontal', h);
          selection.set('layoutAlignVertical', v);
          tool.toolActionComplete({ controller });
        };

        // object:changed fires with a 400ms debounce, so there will be a slight
        // delay in updating the icon for this button when orientation changes.
        useEffect(() => {
          const handleSelectionModified = () => {
            setAlignment([
              selection.firstObject().get('layoutAlignHorizontal'),
              selection.firstObject().get('layoutAlignVertical'),
            ]);
          };

          controller.canvas.on('object:changed', handleSelectionModified);
          return () => {
            controller.canvas.off('object:changed', handleSelectionModified);
          };
        }, [controller.canvas, selection]);

        return (
          <ToolButton
            popup={
              <Popup
                alignment={alignment}
                setCurrentAlignment={setCurrentAlignment}
                selection={selection}
              />
            }
            id='layoutSettings'
            tool={tool}
            maxWidth={400}
          >
            {getIcon(layoutDirection, alignment)}
          </ToolButton>
        );
      }
    );

    return Button;
  }
}

export default ToolLayoutAlignment;
